import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import * as Fire from '@angular/fire/database';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { DbQuery, KeyOf } from 'src/lib/fliko/types/nestedKey.type';
import { Nullable, Nullish } from 'src/lib/fliko/types/typeish.type';
import { AsyncHlp } from '../../helpers/async.helper';

export interface DbObjContext {
  path: string;
  dbRef: Fire.Database;
  name: string;
}

/**
 * Base of Database object and collections
 */
export class DbObjectBase<T> {
  constructor(
    private ctx$: Observable<DbObjContext | null>,
    private query?: DbQuery
  ) {}
  /**
   * Get database observable
   * @return {Observable<Nullable<T>>}
   */
  protected get observe$(): Observable<Nullable<T>> {
    return this.ctx$.pipe(switchMap((ctx) => (ctx ? this.observeContext(ctx) : of(undefined))));
  }

  protected observeContext(ctx: DbObjContext): Observable<Nullable<T>> {
    return Fire.objectVal<T>(Fire.query(Fire.ref(ctx.dbRef, ctx.path), ...this.getConstraints())).pipe(
      catchError((err) => {
        console.error(err.code === 'PERMISSION_DENIED' ? `Permission denied at path '${ctx.name}/${ctx.path}'` : err);
        return of(undefined);
      })
    );
  }

  /** Waits for a database context, then reads one response, including missing values. */
  protected snapshot$(cancel$: Observable<unknown> = EMPTY): Observable<Nullable<T>> {
    return this.ctx$.pipe(
      filter((ctx): ctx is DbObjContext => ctx != null),
      take(1),
      switchMap((ctx) =>
        this.observeContext(ctx).pipe(
          takeUntil(this.ctx$.pipe(filter((current) => current?.path !== ctx.path || current?.dbRef !== ctx.dbRef)))
        )
      ),
      take(1),
      takeUntil(cancel$)
    );
  }

  private getConstraints(): Fire.QueryConstraint[] {
    const arr: Fire.QueryConstraint[] = [];

    if (this.query) {
      // eslint-disable-next-line import/namespace
      if (typeof this.query.by === 'string') arr.push(Fire[`orderBy${this.query.by}`]());
      else arr.push(Fire.orderByChild(this.query!.by.child));
      ObjHlp.forEach(this.query, (key, value) => {
        // eslint-disable-next-line import/namespace
        if (key !== 'by' && value !== undefined) arr.push(Fire[key](value as any));
      });
    }

    return arr;
  }

  /**
   * Peek current existing value of DbObjContext
   */
  protected get peekContext(): Promise<DbObjContext> {
    return AsyncHlp.onFirst(this.ctx$, filter((x) => !!x)) as Promise<DbObjContext>; // prettier-ignore
  }
}

/**
 * Database object
 */
export class DbObject<T> extends DbObjectBase<T> {
  constructor(ctx$: Observable<DbObjContext | null>) {
    super(ctx$);
  }

  /**
   * Get object's observable from db
   * @return {Observable<Nullable<T>>}
   */
  public get(): Observable<Nullable<T>> {
    return this.observe$;
  }

  /**
   * Get first emitted value of object that is not null
   * @return {Promise<Nullable<T>>}
   */
  public once(): Promise<Nullable<T>> {
    return AsyncHlp.onFirst(this.observe$, filter((x) => !!x)); // prettier-ignore
  }

  /**
   * Get first emitted value of object (can be null)
   * @return {Promise<Nullable<T | Nullish>>}
   */
  public first(): Promise<Nullable<T | Nullish>> {
    return AsyncHlp.onFirst(this.observe$);
  }

  /** Reads the first database response and cancels when the caller or context changes. */
  public snapshot(cancel$?: Observable<unknown>): Promise<Nullable<T>> {
    return AsyncHlp.onFirst(this.snapshot$(cancel$));
  }

  /**
   * Set value of object in database (in given path)
   * @param {T} value
   * @return {Promise<void>}
   */
  public async set(value: T): Promise<void> {
    const ctx = await this.peekContext;
    return Fire.set(Fire.ref(ctx.dbRef, ctx.path), value);
  }

  /**
   * Set object's child in database (if rules are stricter)
   * @param {K}    key
   * @param {T[K]} value
   * @return {Promise<void>}
   */
  public async setChild<K extends KeyOf<T>>(key: K, value: T[K]): Promise<void> {
    const ctx = await this.peekContext;
    return Fire.set(Fire.ref(ctx.dbRef, `${ctx.path}/${key}`), value as any);
  }

  /**
   * Update value of object in database (in given path)
   * @param {Partial<T>} value
   * @return {Promise<void>}
   */
  public async update(value: Partial<T>): Promise<void> {
    const ctx = await this.peekContext;
    return Fire.update(Fire.ref(ctx.dbRef, ctx.path), value);
  }

  /**
   * Update object's child in database (if rules are stricter)
   * @param {K}    key
   * @param {T[K]} value
   * @return {Promise<void>}
   */
  public async updateChild<K extends KeyOf<T>>(key: K, value: T[K]): Promise<void> {
    const ctx = await this.peekContext;
    return Fire.update(Fire.ref(ctx.dbRef, `${ctx.path}/${key}`), value as any);
  }

  /**
   * Remove object form database
   * @return {Promise<void>}
   */
  public async remove(): Promise<void> {
    const ctx = await this.peekContext;
    return Fire.remove(Fire.ref(ctx.dbRef, ctx.path));
  }

  // TODO transact
}
